import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { scrollTo } from "./Nav";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1.1, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const yGhost = useTransform(scrollY, [0, 800], [0, 180]);
  const opacityGhost = useTransform(scrollY, [0, 600], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <motion.div
        style={{ y: yGhost, opacity: opacityGhost }}
        aria-hidden
        className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 select-none font-display italic text-[26vw] leading-none text-outline whitespace-nowrap"
      >
        data
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
        <h1 className="font-display leading-[0.92] tracking-tight">
          <Line delay={0.3}>
            <span className="block text-6xl sm:text-7xl lg:text-[9rem] text-cream">SARRON A.</span>
          </Line>
          <Line delay={0.45}>
            <span className="block text-6xl sm:text-7xl lg:text-[9rem] text-lime italic">TADESSE</span>
          </Line>
        </h1>

        <Line delay={0.7}>
          <p className="mt-8 max-w-xl font-mono text-sm md:text-base text-sage leading-relaxed" data-testid="hero-title">
            Data Scientist <span className="text-lime">/</span> Machine Learning{" "}
            <span className="text-lime">/</span> Software &amp; Analytics Engineering
          </p>
        </Line>

        <Line delay={0.85}>
          <div className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-sage">
            <MapPin size={13} className="text-lime" />
            <span data-testid="hero-location">San Francisco, CA</span>
          </div>
        </Line>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-14 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-view-work-button"
            onClick={() => scrollTo("#projects")}
            className="group font-mono text-xs uppercase tracking-[0.25em] bg-lime text-forest px-7 py-4 hover:bg-limedeep transition-colors duration-300"
          >
            View the work
          </button>
          <button
            data-testid="hero-contact-button"
            onClick={() => scrollTo("#contact")}
            className="font-mono text-xs uppercase tracking-[0.25em] border border-treeline text-cream px-7 py-4 hover:border-lime hover:text-lime transition-colors duration-300"
          >
            Get in touch
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-sage hover:text-lime transition-colors duration-300"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
        Scroll
      </motion.button>
    </section>
  );
}
